import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import apiClient from '@/services/client';
import { endpoints } from '@/services/endpoints';

type ViewMode = 'full' | 'step';

interface ModalHistoryEntry {
  tutorialSlug: string;
  viewMode: ViewMode;
  stepId: number | null;
}

interface TutorialModalContextValue {
  isOpen: boolean;
  tutorialSlug: string | null;
  viewMode: ViewMode;
  stepId: number | null;
  openModal: (slug: string, options?: { viewMode?: ViewMode; stepId?: number | null; __fromHistory?: boolean }) => void;
  closeModal: (shouldNavigateToTutorials?: boolean) => void;
  switchToFullView: () => void;
  canGoBackInModal: boolean;
  canGoForwardInModal: boolean;
  goBackInModal: () => void;
  goForwardInModal: () => void;
}

const TutorialModalContext = createContext<TutorialModalContextValue | null>(null);

interface TutorialModalProviderProps {
  children: React.ReactNode;
}

export function TutorialModalProvider({ children }: TutorialModalProviderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [tutorialSlug, setTutorialSlug] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('full');
  const [stepId, setStepId] = useState<number | null>(null);
  const [modalHistoryIndex, setModalHistoryIndex] = useState(-1);

  const openedViaHashRef = useRef(false);
  const previousBodyOverflowRef = useRef<string | null>(null);
  const previousScrollYRef = useRef<number | null>(null);
  const previousBodyPositionRef = useRef<string | null>(null);
  const previousBodyTopRef = useRef<string | null>(null);
  const modalHistoryRef = useRef<ModalHistoryEntry[]>([]);
  const isOpenRef = useRef(false);
  const modalHistoryIndexRef = useRef(-1);

  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  useEffect(() => {
    modalHistoryIndexRef.current = modalHistoryIndex;
  }, [modalHistoryIndex]);

  const pushModalHistoryState = useCallback((entry: ModalHistoryEntry, nextIdx: number) => {
    const url = window.location.pathname + window.location.search + window.location.hash;
    try {
      window.history.pushState(
        { __tutorialModal: true, ...entry, modalIdx: nextIdx },
        '',
        url
      );
    } catch {
      // ignore
    }
  }, []);

  const openModal = useCallback((slug: string, options: { viewMode?: ViewMode; stepId?: number | null; __fromHistory?: boolean } = {}) => {
    setTutorialSlug(slug);
    setViewMode(options.viewMode ?? 'full');
    setStepId(options.stepId ?? null);
    setIsOpen(true);

    if (previousBodyOverflowRef.current === null) {
      previousBodyOverflowRef.current = document.body.style.overflow ?? '';
      previousScrollYRef.current = window.scrollY;
      previousBodyPositionRef.current = document.body.style.position ?? '';
      previousBodyTopRef.current = document.body.style.top ?? '';

      const scrollY = window.scrollY;
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
    }

    if (!window.location.hash?.startsWith('#tutorial-')) {
      openedViaHashRef.current = false;
    }

    if (!options.__fromHistory) {
      const entry: ModalHistoryEntry = {
        tutorialSlug: slug,
        viewMode: options.viewMode ?? 'full',
        stepId: options.stepId ?? null,
      };

      const currentIdx = modalHistoryIndexRef.current;
      const currentEntry = modalHistoryRef.current[currentIdx] ?? null;
      const isSameAsCurrent =
        currentEntry &&
        currentEntry.tutorialSlug === entry.tutorialSlug &&
        currentEntry.viewMode === entry.viewMode &&
        (currentEntry.stepId ?? null) === (entry.stepId ?? null);

      if (!isSameAsCurrent) {
        if (currentIdx < modalHistoryRef.current.length - 1) {
          modalHistoryRef.current = modalHistoryRef.current.slice(0, currentIdx + 1);
        }

        modalHistoryRef.current.push(entry);
        const nextIdx = modalHistoryRef.current.length - 1;
        setModalHistoryIndex(nextIdx);
        pushModalHistoryState(entry, nextIdx);
      }
    }
  }, [pushModalHistoryState]);

  const closeModal = useCallback((shouldNavigateToTutorials = false) => {
    setIsOpen(false);
    setTutorialSlug(null);
    setViewMode('full');
    setStepId(null);

    if (previousBodyOverflowRef.current !== null) {
      document.body.style.overflow = previousBodyOverflowRef.current;
      document.body.style.position = previousBodyPositionRef.current;
      document.body.style.top = previousBodyTopRef.current;
      document.body.style.width = '';

      if (previousScrollYRef.current !== null) {
        window.scrollTo(0, previousScrollYRef.current);
      }

      previousBodyOverflowRef.current = null;
      previousScrollYRef.current = null;
      previousBodyPositionRef.current = null;
      previousBodyTopRef.current = null;
    }

    if (openedViaHashRef.current || shouldNavigateToTutorials) {
      openedViaHashRef.current = false;
      window.dispatchEvent(new CustomEvent('tutorial-modal-close-navigate', {
        detail: { path: '/tutoriais' },
      }));
    } else {
      if (window.location.hash) {
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
      }
    }

    modalHistoryRef.current = [];
    setModalHistoryIndex(-1);
  }, []);

  useEffect(() => {
    const checkHashAndOpenModal = async () => {
      const hash = window.location.hash;
      if (hash?.startsWith('#tutorial-')) {
        const hashValue = hash.substring(10);
        const [shareHash, queryString] = hashValue.split('?');

        try {
          const response = await apiClient.get(endpoints.tutorials.byHash(shareHash));
          const tutorial = response.data?.data;

          if (tutorial?.slug) {
            openedViaHashRef.current = true;

            let stepParam: string | null = null;
            if (queryString) {
              const urlParams = new URLSearchParams(queryString);
              stepParam = urlParams.get('step');
            }

            openModal(tutorial.slug, {
              viewMode: stepParam ? 'step' : 'full',
              stepId: stepParam ? parseInt(stepParam, 10) : null,
            });
          }
        } catch (error) {
          console.error('Erro ao buscar tutorial por hash:', error);
          window.history.replaceState(null, '', window.location.pathname + window.location.search);
        }
      } else {
        openedViaHashRef.current = false;
      }
    };

    checkHashAndOpenModal();

    const handleHashChange = () => {
      checkHashAndOpenModal();
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [openModal]);

  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      if (!isOpenRef.current) return;
      const state = event?.state as { __tutorialModal?: boolean; tutorialSlug?: string; viewMode?: ViewMode; stepId?: number | null; modalIdx?: number } | null;
      if (!state || state.__tutorialModal !== true) return;

      const entry: ModalHistoryEntry = {
        tutorialSlug: state.tutorialSlug ?? '',
        viewMode: state.viewMode ?? 'full',
        stepId: state.stepId ?? null,
      };

      if (typeof state.modalIdx === 'number') {
        setModalHistoryIndex(state.modalIdx);
      } else {
        const idx = modalHistoryRef.current.findIndex(
          e =>
            e.tutorialSlug === entry.tutorialSlug &&
            e.viewMode === entry.viewMode &&
            (e.stepId ?? null) === (entry.stepId ?? null)
        );
        if (idx >= 0) setModalHistoryIndex(idx);
      }

      openModal(entry.tutorialSlug, { viewMode: entry.viewMode, stepId: entry.stepId, __fromHistory: true });
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [openModal]);

  const switchToFullView = useCallback(() => {
    setViewMode('full');
    setStepId(null);
  }, []);

  const canGoBackInModal = modalHistoryIndex > 0;
  const canGoForwardInModal = modalHistoryIndex >= 0 && modalHistoryIndex < modalHistoryRef.current.length - 1;

  const goBackInModal = useCallback(() => {
    if (!canGoBackInModal) return;
    window.history.back();
  }, [canGoBackInModal]);

  const goForwardInModal = useCallback(() => {
    if (!canGoForwardInModal) return;
    window.history.forward();
  }, [canGoForwardInModal]);

  const value: TutorialModalContextValue = {
    isOpen,
    tutorialSlug,
    viewMode,
    stepId,
    openModal,
    closeModal,
    switchToFullView,
    canGoBackInModal,
    canGoForwardInModal,
    goBackInModal,
    goForwardInModal,
  };

  return (
    <TutorialModalContext.Provider value={value}>
      {children}
    </TutorialModalContext.Provider>
  );
}

export function useTutorialModal(): TutorialModalContextValue {
  const context = useContext(TutorialModalContext);
  if (!context) {
    throw new Error('useTutorialModal deve ser usado dentro de um TutorialModalProvider');
  }
  return context;
}
